import React, { useCallback, useState } from 'react';
import { DealerStaticViewer } from '@wix/react-dealer-viewer';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import CloseIconBold from '@wix/da-ds/pkg/Icons/CloseBold';
import ButtonB5 from '@wix/da-shared-react/pkg/Button/ButtonB5';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useDealerBanners, sendDealerCommand } from './useDealerBanners';
import Banner from './_partials/Banner';
import ComplexBanner from './_partials/ComplexBanner';
import { DaOffer, Platform } from './types';

import s from './DealerAd.scss';

export interface Props {
  realEstateId: string;
  theme: string;
  width?: number;
  height?: number;
  userGuid: string;
  biClientId: string;
  biClientIdSigned: string;
  additionalParams?: { [s: string]: string }; // TODO remove if we dont use it
  withCloseButton: boolean;
  translationVersion: string;
  platform: Platform;
}

const DealerAd: React.FC<Props> = ({
  realEstateId,
  width = 0,
  height = 0,
  userGuid = '',
  additionalParams,
  biClientId,
  withCloseButton,
  translationVersion,
  biClientIdSigned,
  platform,
}) => {
  const { isEmpty, isLoaded, offers, onEmpty, onCtaClick, closeOffer } =
    useDealerBanners(
      realEstateId,
      biClientIdSigned,
      userGuid === '' ? biClientId : userGuid
    );
  // When we tell parent to do something like show modal or adhesion
  const [chromeSignalSent, setChromeSignalSent] = useState(false);

  const ctaClick = useCallback(
    (url: string, o: DaOffer) => {
      onCtaClick(o, url);
    },
    [onCtaClick]
  );

  if (isEmpty || (isLoaded && !offers.length)) {
    return null; // collapse
  }

  if (!offers.length) {
    return null;
  }

  // New-world offers with 'creative' content instead of 'payloadJson'
  // These we have to render/handle ourselves
  const theOffer = offers[0].asset;
  if (isLoaded && theOffer.creative) {
    const usedOffer = offers[0];
    const creative = theOffer.creative;
    switch (creative.creativeType) {
      case 'modal':
        if (!chromeSignalSent) {
          setChromeSignalSent(true);
          sendDealerCommand('show-modal', realEstateId, creative?.modal);
        }
        return;

      case 'banner':
        return (
          <ErrorBoundary debugComponent="DealerAdComplexBanner">
            <div className={s['root']}>
              <Banner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.banner!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'adhesion':
        if (!chromeSignalSent) {
          setChromeSignalSent(true);
          sendDealerCommand('show-adhesion', realEstateId, usedOffer);
        }
        return (
          <ErrorBoundary debugComponent="DealerAd">
            <div className={s['root']}>
              <Banner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.adhesion!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'complexbanner':
        return (
          <ErrorBoundary debugComponent="DealerAdComplexBanner">
            <div className={s['root']}>
              <ComplexBanner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.complexbanner!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );
      default:
    }
    return null;
  }

  // Old-world offers with a 'payloadJson', pass to react-dealer-viewer
  const offer = offers[0];
  return (
    <ErrorBoundary debugComponent="DealerAd">
      <div className={s['root']}>
        {isLoaded && (
          <div key={offer.offerGuid} className={s['banner-wrapper']}>
            <DealerStaticViewer
              dealerOptions={additionalParams}
              bannerPosition={realEstateId}
              locale="en"
              metasiteGuid=""
              translationsUrl={`https://static.parastorage.com/services/dealer-cms-translations/${translationVersion}/`}
              offer={offer}
              additionalBiParams={{
                _uuid: userGuid || '',
                uuid: userGuid || '',
                client_id: biClientId || '',
              }}
              onEmpty={onEmpty}
              onError={onEmpty}
            />

            {withCloseButton && (
              <ButtonB5
                className={s['close-button']}
                icon={CloseIconBold}
                iconProps={{ size: IconSize.MEDIUM }}
                onClick={() => closeOffer(offer)}
                biData={{
                  evid: BiEventType.CLOSE_BANNER_AD,
                  realestateid: realEstateId,
                  campaignid: offer.offerGuid,
                }}
              />
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
DealerAd.displayName = 'DealerAd';

// we just don't care for prop changes, period
export default React.memo(DealerAd, () => true);
